import { useCallback, useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { useSetCallNoteMutation } from "services/dialstack";

import {
  noteDraftChanged,
  noteSaveFailed,
  noteSaveStarted,
  noteSaved,
  selectCallNoteDraft
} from "../call-notes.slice";

export const CALL_NOTE_SAVE_DELAY_MS = 1000;

// The phone note typed on the workbench (ENG-2226). Autosaves to
// `PUT /dialstack/calls/<id>/note/` a second after the last keystroke; the
// draft and its state live in the store so the text survives the pop folding
// or the phone closing. `backendCallId` is the Suno Call id the pop carries.
export default function useCallNote(backendCallId: number | null) {
  const dispatch = useAppDispatch();
  const draft = useAppSelector((state) =>
    selectCallNoteDraft(state, backendCallId)
  );
  const [setCallNote] = useSetCallNoteMutation();
  const timer = useRef<number | null>(null);
  const latest = useRef(draft?.text ?? "");
  latest.current = draft?.text ?? "";

  const save = useCallback(
    async (callId: number, text: string) => {
      dispatch(noteSaveStarted({ callId }));
      try {
        await setCallNote({ callId, text }).unwrap();
        dispatch(noteSaved({ callId, text }));
      } catch {
        dispatch(noteSaveFailed({ callId }));
      }
    },
    [dispatch, setCallNote]
  );

  const setText = useCallback(
    (text: string) => {
      if (backendCallId === null) return;
      dispatch(noteDraftChanged({ callId: backendCallId, text }));
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        save(backendCallId, text);
      }, CALL_NOTE_SAVE_DELAY_MS);
    },
    [backendCallId, dispatch, save]
  );

  // Unmounting mid-debounce (the pop folds, the phone closes) must not drop
  // the keystrokes: flush straight away.
  useEffect(
    () => () => {
      if (timer.current === null || backendCallId === null) return;
      window.clearTimeout(timer.current);
      timer.current = null;
      save(backendCallId, latest.current);
    },
    [backendCallId, save]
  );

  return {
    text: draft?.text ?? "",
    status: draft?.status ?? "idle",
    setText
  };
}
